import { arraysHaveCommonItems } from '@/utils/arrays';
import { getUserProfile, postCurrentOffice } from '@/domains/authentication/api/authentication.api';
import { handleRenderError } from '@/domains/common/services/errorHandler';
import RoleEnum from '@/domains/authentication/typescript/RoleEnum';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type OfficeInterface from '@/domains/office/typescript/OfficeInterface';
import type UserInterface from '@/domains/authentication/typescript/UserInterface';
import type UserOfficeInterface from '@/domains/authentication/typescript/UserOfficeInterface';
import type UserProfileInterface from '@/domains/authentication/typescript/UserProfileInterface';
import type OfficeUserInterface from '@/domains/office/typescript/OfficeUserInterface';

export default defineStore('auth', () => {
    const selectedUserOffice = ref<OfficeInterface | null>(null);
    const isLoadingUser = ref(true);
    const isUserLoggedIn = ref(false);
    const userId = ref<string>();
    const userRootRoles = ref<RoleEnum[]>([]);
    const userOffices = ref<UserOfficeInterface[]>([]);
    const userProfile = ref<UserProfileInterface | null>(null);
    const currentOfficeUser = ref<OfficeUserInterface>();

    const isAdminErgo = computed(() => isGranted(RoleEnum.ROLE_ADMIN_ERGO));
    const hasOfficeWideAccess = computed(
        () => isGranted(RoleEnum.ROLE_DOCTOR_GLOBAL) || isGranted(RoleEnum.ROLE_ADMINISTRATOR_OFFICE),
    );

    const updateUserProfile = async (userOffice: UserOfficeInterface): Promise<void> => {
        if (userOffice) {
            const officeUserDTO = {
                user: `/users/${userId.value}`,
                office: `/offices/${userOffice.office.id}`,
            };
            await postCurrentOffice(userOffice.id, officeUserDTO);
            userProfile.value = await getUserProfile(userOffice.id);
            selectedUserOffice.value = userOffice.office;
        }
    };

    const initUserInfos = async (payload: UserInterface): Promise<void> => {
        userRootRoles.value = payload?.roles ?? [];
        userId.value = payload?.id ?? undefined;
        if (isGranted(RoleEnum.ROLE_ADMIN_ERGO)) {
            return;
        }
        userOffices.value = payload?.officeUsers ?? undefined;
        if (userOffices.value !== undefined && userOffices.value.length > 0) {
            let userOffice: UserOfficeInterface;

            if (payload.currentOfficeUser) {
                currentOfficeUser.value = payload?.currentOfficeUser as OfficeUserInterface;
                selectedUserOffice.value = payload.currentOfficeUser.office;
            }

            if (userOffices.value.length === 1) {
                userOffice = userOffices.value[0];
            } else {
                const selectedOffice = userOffices.value?.find((office) => {
                    return office.office.id === selectedUserOffice.value?.id;
                });
                userOffice = selectedOffice ?? userOffices.value[0];
            }
            await updateUserProfile(userOffice);
            return;
        }

        handleRenderError(new Error('auth.error.no_office'), null, 'auth.error.no_office');
        Promise.reject(new Error(`No office found for user ${payload.id}`));
    };

    function getUserOfficeById(officeId: string) {
        return userOffices.value.find((office) => office.office.id === officeId);
    }

    const offices = computed<OfficeInterface[]>(() => {
        return userOffices.value.map((user) => {
            return user.office;
        });
    });

    function mergeUserOfficeRolesInUserRoles(userRoles: RoleEnum[], userOfficeRoles: RoleEnum[]): RoleEnum[] {
        const roles: RoleEnum[] = [...userRoles];

        userOfficeRoles.forEach((role) => {
            if (!roles.includes(role)) {
                roles.push(role);
            }
        });

        return roles;
    }

    const roles = computed<RoleEnum[]>(() => {
        if (userProfile.value !== null) {
            return mergeUserOfficeRolesInUserRoles(userRootRoles.value, userProfile.value.roles);
        }

        return userRootRoles.value;
    });

    // used to conditionally display UI elements
    const isUserAuthorized = (rolesNeeded: RoleEnum[] = []) => {
        if (!rolesNeeded.length) {
            return true;
        }
        return arraysHaveCommonItems(rolesNeeded, roles.value);
    };

    const isGranted = (role: RoleEnum) => {
        return roles.value.includes(role);
    };

    function $reset() {
        console.info('reset auth store');
        selectedUserOffice.value = null;
        isUserLoggedIn.value = false;
        userId.value = undefined;
        userProfile.value = null;
        userRootRoles.value = [];
        userOffices.value = [];
        // Firstname, lastname and email of the practitioner who just left.
        currentOfficeUser.value = undefined;
        // isLoadingUser is deliberately not restored to its initial true: the router calls
        // updateToken() whenever it is true, so a "complete" reset would send the logout redirect
        // back through /users/current-user and, on a surviving cookie, straight into the account.
    }

    return {
        selectedUserOffice,
        isUserLoggedIn,
        isLoadingUser,
        userId,
        userProfile,
        isUserAuthorized,
        initUserInfos,
        updateUserProfile,
        getUserOfficeById,
        roles,
        offices,
        isGranted,
        isAdminErgo,
        hasOfficeWideAccess,
        currentOfficeUser,

        $reset,
    };
});
